/**
 * useCamera — useTorchMode feature hook
 *
 * Manages the 3-state torch mode (off → auto → on → off) with ambient
 * brightness hysteresis. Extracted from CameraModal for composability.
 *
 * - 'off':  torch forced off
 * - 'auto': torch controlled by ambient brightness hysteresis
 * - 'on':   torch forced on
 *
 * Hysteresis: torch turns on below brightness 100 (dim indoor), turns off
 * above brightness 160 (well-lit) with a 3-second debounce to prevent
 * oscillation when a white document reflects torch light back.
 *
 * The user's preference is persisted to localStorage and restored on open.
 * When `darkModeEnhance` is true, defaults to 'auto'; otherwise 'off'.
 *
 * Usage:
 * ```tsx
 * const torch = useTorchMode({
 *   isOpen, stream, torchEnabled, torchSupported, toggleTorch,
 *   ambientBrightness, darkModeEnhance, reviewPaused, isReviewPausedRef,
 * });
 * // torch.torchMode, torch.handleTorchToggle, torch.showTorchPrompt
 * ```
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { haptics } from '@/lib/haptics';
import { AUTO_TORCH_PREF_KEY } from './constants';

export type TorchMode = 'off' | 'on' | 'auto';

export interface UseTorchModeParams {
  /** Whether the camera modal is open */
  isOpen: boolean;
  /** Current media stream (null if not started) */
  stream: MediaStream | null;
  /** Whether torch is currently on (from useCamera) */
  torchEnabled: boolean;
  /** Whether torch is supported on this track (from useCamera) */
  torchSupported: boolean;
  /** Toggle torch on/off (from useCamera) */
  toggleTorch: () => Promise<void>;
  /** Current ambient brightness 0–255 (from useCamera) */
  ambientBrightness: number | null;
  /** Enable dark mode enhancement — defaults torch to 'auto' (from CameraModal prop) */
  darkModeEnhance: boolean;
  /** Whether the user is reviewing a captured photo (pauses auto-torch) */
  reviewPaused: boolean;
  /** Ref to reviewPaused for use in effects without re-running */
  isReviewPausedRef: React.MutableRefObject<boolean>;
}

export interface UseTorchModeReturn {
  /** Current torch mode */
  torchMode: TorchMode;
  /** Cycle torch mode: off → auto → on → off */
  handleTorchToggle: () => Promise<void>;
  /** Whether to show the "tap to enable flash" prompt (low light + torch not on) */
  showTorchPrompt: boolean;
  /** Whether torch is currently being probed/recovered */
  isTorchChecking: boolean;
}

export function useTorchMode({
  isOpen,
  stream,
  torchEnabled,
  torchSupported,
  toggleTorch,
  ambientBrightness,
  darkModeEnhance,
  reviewPaused,
  isReviewPausedRef,
}: UseTorchModeParams): UseTorchModeReturn {
  const [torchMode, setTorchMode] = useState<TorchMode>('off');
  const torchModeRef = useRef<TorchMode>(torchMode);
  torchModeRef.current = torchMode;
  const autoTorchPendingRef = useRef(false);
  const autoTorchOffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs so handleTorchToggle can read the latest torch state and call the
  // latest toggleTorch without having them in its dependency array.
  // toggleTorch depends on zoomLevel (which changes every frame during
  // auto-zoom), so including it in deps would recreate the callback constantly.
  const toggleTorchRef = useRef(toggleTorch);
  useEffect(() => {
    toggleTorchRef.current = toggleTorch;
  }, [toggleTorch]);
  const torchEnabledRef = useRef(torchEnabled);
  useEffect(() => {
    torchEnabledRef.current = torchEnabled;
  }, [torchEnabled]);

  // ---- Torch mode toggle: cycle off → auto → on → off ----
  const handleTorchToggle = useCallback(async () => {
    const prev = torchModeRef.current;
    const next: TorchMode = prev === 'off' ? 'auto' : prev === 'auto' ? 'on' : 'off';
    setTorchMode(next);
    void haptics.toggle();
    torchModeRef.current = next;
    try { localStorage.setItem(AUTO_TORCH_PREF_KEY, next); } catch { /* ignore */ }

    // For 'on': turn torch on if it's off. For 'off': turn torch off if it's on.
    // For 'auto': let the hysteresis effect handle it.
    if ((next === 'on' && !torchEnabledRef.current) || (next === 'off' && torchEnabledRef.current)) {
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    }
  }, []);

  // ---- Torch mode: 'auto' hysteresis effect ----
  useEffect(() => {
    if (!isOpen || !stream || torchMode !== 'auto' || isReviewPausedRef.current) return;
    if (autoTorchPendingRef.current) return;

    const HYSTERESIS_LOW = 100;
    const HYSTERESIS_HIGH = 160;
    const OFF_DEBOUNCE_MS = 3000;

    if (ambientBrightness !== null && ambientBrightness < HYSTERESIS_LOW && !torchEnabled) {
      if (autoTorchOffTimerRef.current) {
        clearTimeout(autoTorchOffTimerRef.current);
        autoTorchOffTimerRef.current = null;
      }
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    } else if (ambientBrightness !== null && ambientBrightness > HYSTERESIS_HIGH && torchEnabled) {
      if (!autoTorchOffTimerRef.current) {
        autoTorchOffTimerRef.current = setTimeout(() => {
          autoTorchOffTimerRef.current = null;
          if (autoTorchPendingRef.current) return;
          if (torchModeRef.current !== 'auto') return;
          autoTorchPendingRef.current = true;
          void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
        }, OFF_DEBOUNCE_MS);
      }
    } else {
      if (autoTorchOffTimerRef.current) {
        clearTimeout(autoTorchOffTimerRef.current);
        autoTorchOffTimerRef.current = null;
      }
    }
  }, [ambientBrightness, torchEnabled, isOpen, stream, torchMode, reviewPaused, isReviewPausedRef]);

  // Sync torch mode with localStorage on open.
  useEffect(() => {
    if (isOpen) {
      try {
        const stored = localStorage.getItem(AUTO_TORCH_PREF_KEY);
        const mode: TorchMode = (stored === 'off' || stored === 'on' || stored === 'auto')
          ? stored
          : (darkModeEnhance ? 'auto' : 'off');
        setTorchMode(mode);
        torchModeRef.current = mode;
      } catch {
        const mode: TorchMode = darkModeEnhance ? 'auto' : 'off';
        setTorchMode(mode);
        torchModeRef.current = mode;
      }
    } else {
      setTorchMode('off');
      torchModeRef.current = 'off';
    }
  }, [darkModeEnhance, isOpen]);

  // Apply torch state when the camera opens or torch support is detected.
  useEffect(() => {
    if (!isOpen || !stream || isReviewPausedRef.current) return;
    if (autoTorchPendingRef.current) return;
    const mode = torchModeRef.current;
    if (mode === 'on' && !torchEnabled) {
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    } else if (mode === 'off' && torchEnabled) {
      autoTorchPendingRef.current = true;
      void toggleTorchRef.current().finally(() => { autoTorchPendingRef.current = false; });
    }
  }, [isOpen, stream, torchSupported, torchEnabled, torchMode, reviewPaused, isReviewPausedRef]);

  // Cleanup off-timer on unmount
  useEffect(() => {
    return () => {
      if (autoTorchOffTimerRef.current) {
        clearTimeout(autoTorchOffTimerRef.current);
      }
    };
  }, []);

  // Low-light / torch-enable prompt.
  // In a WebView (Capacitor), applyConstraints({ torch: true }) requires a
  // user gesture — like audio autoplay. Effect-initiated torch calls fail
  // silently on camera open. So when torchMode is 'on' or 'auto' and the torch
  // isn't on yet, show a tappable prompt.
  const isLowLight = ambientBrightness !== null && ambientBrightness < 100;
  const showTorchPrompt = !torchEnabled && torchSupported && (
    torchMode === 'on' || (torchMode === 'auto' && isLowLight)
  );

  return {
    torchMode,
    handleTorchToggle,
    showTorchPrompt,
    isTorchChecking: autoTorchPendingRef.current,
  };
}
